import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon, tokens } from '@/design';
import { MM_PER_INCH } from './pageUnits';
import { PAGE_PRESET_GROUPS, PAGE_PRESETS } from './pagePresets';
import type { PagePresetId, PageUnits } from './layoutTypes';

export interface PageSizePickerProps {
  readonly value: PagePresetId;
  readonly widthMm: number;
  readonly heightMm: number;
  readonly units: PageUnits;
  readonly onPreset: (preset: Exclude<PagePresetId, 'custom'>) => void;
  readonly onCustom: (widthMm: number, heightMm: number, units: PageUnits) => void;
}

function PaperPreview({
  widthMm,
  heightMm,
  selected,
}: {
  readonly widthMm: number;
  readonly heightMm: number;
  readonly selected: boolean;
}) {
  const max = Math.max(widthMm, heightMm);
  const width = Math.max(12, (widthMm / max) * 48);
  const height = Math.max(12, (heightMm / max) * 48);
  return (
    <span
      aria-hidden
      style={{
        position: 'relative',
        display: 'block',
        width,
        height,
        background: tokens.color.canvas.light,
        border: `${tokens.border.width.thin}px solid ${
          selected ? tokens.color.accent.primary : tokens.color.ink.muted
        }`,
        boxShadow: tokens.elevation[1],
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(3, height * 0.17),
          background: tokens.color.semantic.info,
          opacity: 0.35,
        }}
      />
    </span>
  );
}

function displayName(value: PagePresetId): string {
  if (value === 'custom') return 'Custom';
  const preset = PAGE_PRESETS[value];
  return `${preset.name} ${preset.orientation}`;
}

function toDisplayUnits(mm: number, units: PageUnits): number {
  return units === 'in' ? mm / MM_PER_INCH : mm;
}

export function PageSizePicker({
  value,
  widthMm,
  heightMm,
  units,
  onPreset,
  onCustom,
}: PageSizePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customUnits, setCustomUnits] = useState<PageUnits>(units);
  const [customWidth, setCustomWidth] = useState(() =>
    String(Number(toDisplayUnits(widthMm, units).toFixed(2))),
  );
  const [customHeight, setCustomHeight] = useState(() =>
    String(Number(toDisplayUnits(heightMm, units).toFixed(2))),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const inputStyle: CSSProperties = {
    minHeight: tokens.density.touchMin,
    width: '100%',
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };

  const showCustom = () => {
    setCustomUnits(units);
    setCustomWidth(String(Number(toDisplayUnits(widthMm, units).toFixed(2))));
    setCustomHeight(String(Number(toDisplayUnits(heightMm, units).toFixed(2))));
    setError(null);
    setCustomOpen(true);
  };

  const applyCustom = () => {
    const width = Number(customWidth);
    const height = Number(customHeight);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      setError('Enter a valid positive width and height.');
      return;
    }
    const nextWidthMm = customUnits === 'in' ? width * MM_PER_INCH : width;
    const nextHeightMm = customUnits === 'in' ? height * MM_PER_INCH : height;
    if (
      nextWidthMm < 10
      || nextHeightMm < 10
      || nextWidthMm > 5000
      || nextHeightMm > 5000
    ) {
      setError('Page dimensions must be between 10 mm and 5000 mm.');
      return;
    }
    onCustom(nextWidthMm, nextHeightMm, customUnits);
    setCustomOpen(false);
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label="Open page size gallery"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        style={{
          minHeight: tokens.density.touchMin,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
          padding: `0 ${tokens.space[2]}px`,
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          background: open ? tokens.color.accent.subtle : tokens.color.surface[0],
          color: tokens.color.ink.primary,
          cursor: 'pointer',
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.xs,
        }}
      >
        <Icon name="description" size={16} decorative />
        {displayName(value)}
        <Icon name={open ? 'expand_less' : 'expand_more'} size={16} decorative />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Page size gallery"
          data-testid="page-size-gallery"
          style={{
            position: 'absolute',
            zIndex: tokens.zIndex.modal,
            top: `calc(100% + ${tokens.space[1]}px)`,
            left: 0,
            width: `min(${tokens.layout.standaloneStateExtraWide}px, calc(100vw - ${tokens.space[4] * 2}px))`,
            maxHeight: '70vh',
            overflowY: 'auto',
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.lg,
            boxShadow: tokens.elevation[3],
            fontFamily: tokens.font.family.ui,
          }}
        >
          {PAGE_PRESET_GROUPS.map((group) => (
            <section
              key={group.id}
              aria-labelledby={`page-group-${group.id}`}
              style={{ padding: tokens.space[3], borderBottom: tokens.border.thin }}
            >
              <h3
                id={`page-group-${group.id}`}
                style={{
                  margin: `0 0 ${tokens.space[3]}px`,
                  fontSize: tokens.font.size.sm,
                }}
              >
                {group.label}
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fit, minmax(${tokens.density.touchMin * 2.5}px, 1fr))`,
                  gap: tokens.space[2],
                }}
              >
                {group.presets.map((preset) => {
                  const selected = value === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      aria-label={`Use ${preset.name} ${preset.orientation}, ${preset.displayDimensions}`}
                      aria-pressed={selected}
                      onClick={() => {
                        onPreset(preset.id);
                        setOpen(false);
                      }}
                      style={{
                        minHeight: 106,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: tokens.space[1],
                        padding: tokens.space[2],
                        border: selected
                          ? `${tokens.focus.ringWidth}px solid ${tokens.color.accent.primary}`
                          : tokens.border.thin,
                        borderRadius: tokens.radius.input,
                        background: selected
                          ? tokens.color.accent.subtle
                          : tokens.color.surface[0],
                        color: tokens.color.ink.primary,
                        cursor: 'pointer',
                        fontFamily: tokens.font.family.ui,
                      }}
                    >
                      <span
                        style={{
                          minHeight: 50,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PaperPreview
                          widthMm={preset.widthMm}
                          heightMm={preset.heightMm}
                          selected={selected}
                        />
                      </span>
                      <strong style={{ fontSize: tokens.font.size.xs }}>{preset.name}</strong>
                      <span style={{ fontSize: 10, color: tokens.color.ink.secondary }}>
                        {preset.displayDimensions}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
          <div style={{ padding: tokens.space[3] }}>
            <button
              type="button"
              aria-label="Create custom page size"
              onClick={showCustom}
              style={{
                minHeight: tokens.density.touchMin,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: tokens.space[2],
                padding: `0 ${tokens.space[2]}px`,
                border: 0,
                borderRadius: tokens.radius.input,
                background: 'transparent',
                color: tokens.color.ink.primary,
                cursor: 'pointer',
                fontFamily: tokens.font.family.ui,
              }}
            >
              <Icon name="tune" size={16} decorative />
              Custom page size…
            </button>
          </div>
        </div>
      ) : null}
      {customOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Custom page size"
          style={{
            position: 'fixed',
            zIndex: tokens.zIndex.modal,
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: tokens.color.overlay.scrim,
          }}
        >
          <div
            style={{
              width: 340,
              padding: tokens.space[4],
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: tokens.border.thin,
              borderRadius: tokens.radius.lg,
              boxShadow: tokens.elevation[3],
              fontFamily: tokens.font.family.ui,
            }}
          >
            <h2 style={{ margin: `0 0 ${tokens.space[4]}px`, fontSize: tokens.font.size.lg }}>
              Custom page size
            </h2>
            <label style={{ display: 'grid', gap: tokens.space[1], fontSize: tokens.font.size.xs }}>
              Units
              <select
                aria-label="Custom page units"
                value={customUnits}
                onChange={(event) => {
                  const next = event.target.value as PageUnits;
                  const widthValue = Number(customWidth);
                  const heightValue = Number(customHeight);
                  if (Number.isFinite(widthValue) && Number.isFinite(heightValue)) {
                    const factor = next === 'in' ? 1 / MM_PER_INCH : MM_PER_INCH;
                    setCustomWidth(String(Number((widthValue * factor).toFixed(2))));
                    setCustomHeight(String(Number((heightValue * factor).toFixed(2))));
                  }
                  setCustomUnits(next);
                }}
                style={inputStyle}
              >
                <option value="mm">Millimetres</option>
                <option value="in">Inches</option>
              </select>
            </label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'end',
                gap: tokens.space[2],
                marginTop: tokens.space[3],
              }}
            >
              <label style={{ display: 'grid', gap: tokens.space[1], fontSize: tokens.font.size.xs }}>
                Width
                <input
                  aria-label="Custom page width"
                  type="number"
                  min="0.4"
                  step="0.01"
                  value={customWidth}
                  onChange={(event) => setCustomWidth(event.target.value)}
                  style={inputStyle}
                />
              </label>
              <button
                type="button"
                aria-label="Swap page width and height"
                title="Swap orientation"
                onClick={() => {
                  setCustomWidth(customHeight);
                  setCustomHeight(customWidth);
                }}
                style={{
                  minWidth: tokens.density.touchMin,
                  minHeight: tokens.density.touchMin,
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.input,
                  background: tokens.color.surface[0],
                  color: tokens.color.ink.primary,
                  cursor: 'pointer',
                }}
              >
                ↔
              </button>
              <label style={{ display: 'grid', gap: tokens.space[1], fontSize: tokens.font.size.xs }}>
                Height
                <input
                  aria-label="Custom page height"
                  type="number"
                  min="0.4"
                  step="0.01"
                  value={customHeight}
                  onChange={(event) => setCustomHeight(event.target.value)}
                  style={inputStyle}
                />
              </label>
            </div>
            {error ? (
              <div role="alert" style={{ marginTop: tokens.space[2], color: tokens.color.semantic.danger, fontSize: tokens.font.size.xs }}>
                {error}
              </div>
            ) : null}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: tokens.space[2],
                marginTop: tokens.space[4],
              }}
            >
              <button
                type="button"
                onClick={() => setCustomOpen(false)}
                style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyCustom}
                style={{
                  ...inputStyle,
                  width: 'auto',
                  cursor: 'pointer',
                  background: tokens.color.accent.primary,
                  color: tokens.color.accent.on,
                  border: 0,
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
